import { AxiosError } from 'axios';

type TPriceErrorData = {
	price: number;
	minPrice: number;
	maxPrice: number;
};

const MIN_KILOMETERS = 100;

const KILOMETERS_MIN_ERROR_MESSAGE =
	'A quilometragem deve ser superior a 100 km.';

const NETWORK_ERROR_MESSAGE =
	'Falha na conexão. Verifique sua internet e tente novamente.';

const PHOTO_UPLOAD_ERROR_CODE = 'Falha no upload da foto';

const PRICE_OUT_OF_RANGE_REGEX =
	/Price\s+(\d+(?:\.\d+)?)\s+is\s+outside\s+valid\s+range\s*\(\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*\)/i;

const ENGLISH_API_MESSAGE_PATTERNS = [
	/price\s+validation/i,
	/outside\s+valid\s+range/i,
	/validation\s+failed/i,
	/internal\s+server\s+error/i,
	/^error\b/i,
	/\bfailed\b/i,
	/\binvalid\b/i,
	/\bunable\b/i,
	/\bcannot\b/i
] as const;

const isKilometersApiError = (message: string): boolean => {
	const normalized = message.toLowerCase();

	if (/kilometers?\s+validation/i.test(message)) return true;
	if (/mileage\s+validation/i.test(message)) return true;

	if (normalized.includes('quilometragem')) {
		const invalidHints = ['invalid', 'inválid', 'minimum', 'mínim'];
		if (invalidHints.some((hint) => normalized.includes(hint))) return true;
	}

	const requiresMinimum =
		/(?:greater|more|at least|superior)/i.test(message) &&
		/(?:100\b|1,?00\b)/.test(message);
	if (requiresMinimum) return true;

	if (/kilometers?\s+\d+/i.test(message)) {
		const constraintHints = ['greater', 'less', 'outside', 'invalid', 'minimum'];
		if (constraintHints.some((hint) => normalized.includes(hint))) return true;
	}

	return false;
};

const PORTUGUESE_HINT_PATTERN =
	/[àáâãéêíóôõúçÀÁÂÃÉÊÍÓÔÕÚÇ]|\b(nao|não|erro|anuncio|anúncio|tente|novamente|inválido|invalido|disponível|disponivel|possível|possivel|servidor|informe|obrigatório|obrigatorio|sucesso|excluir|desativar|reativar|vendido|plano|foto|salvar|alterações|alteracoes)\b/i;

const parsePriceValidationError = (errorMessage: string): TPriceErrorData | null => {
	const match = errorMessage.match(PRICE_OUT_OF_RANGE_REGEX);
	const [, price, minPrice, maxPrice] = match ?? [];
	if (!price || !minPrice || !maxPrice) return null;

	return {
		price: parseFloat(price),
		minPrice: parseFloat(minPrice),
		maxPrice: parseFloat(maxPrice)
	};
};

const formatCurrency = (value: number): string =>
	new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const readApiMessage = (error: unknown): string | undefined => {
	if (error instanceof AxiosError) {
		const message = (error.response?.data as { message?: unknown } | undefined)?.message;
		return typeof message === 'string' && message.trim().length > 0 ? message.trim() : undefined;
	}

	if (typeof error === 'object' && error !== null && 'response' in error) {
		const response = (error as { response?: { data?: { message?: unknown } } }).response;
		const message = response?.data?.message;
		return typeof message === 'string' && message.trim().length > 0 ? message.trim() : undefined;
	}

	if (error instanceof Error && error.message.trim().length > 0) {
		return error.message.trim();
	}

	return undefined;
};

const isLikelyEnglishApiMessage = (message: string): boolean =>
	ENGLISH_API_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));

const isLikelyPortugueseMessage = (message: string): boolean =>
	PORTUGUESE_HINT_PATTERN.test(message) && !isLikelyEnglishApiMessage(message);

const getKilometersErrorMessage = (error: unknown): string | null => {
	const apiMessage = readApiMessage(error);
	const errorMessage = apiMessage ?? (error instanceof Error ? error.message : '');
	if (!errorMessage.trim()) return null;

	const isKmError = isKilometersApiError(errorMessage);
	if (!isKmError) return null;

	return KILOMETERS_MIN_ERROR_MESSAGE;
};

const getPriceErrorMessage = (error: unknown): string | null => {
	const apiMessage = readApiMessage(error);
	const errorMessage = apiMessage ?? (error instanceof Error ? error.message : '');
	const parsedError = parsePriceValidationError(errorMessage);
	if (!parsedError) return null;

	const { price, minPrice, maxPrice } = parsedError;
	return `O preço ${formatCurrency(price)} está fora da faixa permitida. O valor deve estar entre ${formatCurrency(minPrice)} e ${formatCurrency(maxPrice)}.`;
};

const getUserFacingApiErrorMessage = (error: unknown, fallback: string): string => {
	if (error instanceof Error && error.message === PHOTO_UPLOAD_ERROR_CODE) {
		return 'Não foi possível enviar a foto. Tente novamente.';
	}

	const priceMessage = getPriceErrorMessage(error);
	if (priceMessage) return priceMessage;

	const kilometersMessage = getKilometersErrorMessage(error);
	if (kilometersMessage) return kilometersMessage;

	if (error instanceof AxiosError && !error.response?.status) {
		return NETWORK_ERROR_MESSAGE;
	}

	const apiMessage = readApiMessage(error);
	if (apiMessage && isLikelyPortugueseMessage(apiMessage)) {
		return apiMessage;
	}

	return fallback;
};

export {
	KILOMETERS_MIN_ERROR_MESSAGE,
	MIN_KILOMETERS,
	NETWORK_ERROR_MESSAGE,
	PHOTO_UPLOAD_ERROR_CODE,
	getKilometersErrorMessage,
	getPriceErrorMessage,
	getUserFacingApiErrorMessage,
	isLikelyEnglishApiMessage,
	isLikelyPortugueseMessage,
	readApiMessage
};
